import * as React from "react"
import { Check, Lock, Pause } from "lucide-react"

import { cn } from "@/lib/utils"
import { EMPTY } from "@/lib/format"
import { getStatusMeta, toneClass } from "@/lib/status"

const STATUS_ICONS = { pause: Pause, check: Check, lock: Lock }

/**
 * StatusPill — dot + label status badge (never colour alone).
 *
 *   <StatusPill status="Payment Received" />                 navy "Payment Received"
 *   <StatusPill status="PENDING" domain="shop_drawing" />     blue "Pending"
 *   <StatusPill status="Overdue" label="Overdue · 48 d" />    red, custom text
 *   <StatusPill tone="violet" label="Variation" />            explicit tone
 *
 * Props: status, domain (see lib/status.js STATUS_DOMAINS), label, tone, size 'sm'|'md'|'lg',
 * shape 'pill'|'square', icon (lucide component replacing the dot), hideDot, className.
 * Empty status and no label → muted '—'.
 */
const StatusPill = React.forwardRef(({
  status,
  domain,
  label,
  tone,
  size = "md",
  shape = "pill",
  icon,
  hideDot = false,
  className,
  title,
  ...props
}, ref) => {
  const meta = getStatusMeta(status, domain)
  const text = label ?? meta.label

  if (!text) {
    return (
      <span ref={ref} className={cn("text-ink-4", className)} data-status-empty="true" {...props}>
        <span aria-hidden="true">{EMPTY}</span>
        <span className="sr-only">No status</span>
      </span>
    )
  }

  const resolvedTone = tone || meta.tone
  const Icon = icon || STATUS_ICONS[meta.icon]

  return (
    <span
      ref={ref}
      className={cn("ui-status-pill", toneClass(resolvedTone), className)}
      data-tone={resolvedTone}
      data-status={meta.key || undefined}
      data-size={size === "md" ? undefined : size}
      data-shape={shape === "square" ? "square" : undefined}
      title={title ?? (typeof text === "string" && text.length > 20 ? text : undefined)}
      {...props}
    >
      {Icon ? <Icon aria-hidden="true" strokeWidth={2.5} /> : null}
      {!Icon && !hideDot ? <span className="ui-tone-dot" aria-hidden="true" /> : null}
      <span className="ui-status-label">{text}</span>
    </span>
  )
})
StatusPill.displayName = "StatusPill"

/** ToneDot — the 8px status dot alone, for legends and inline attention links. */
function ToneDot({ tone = "gray", size = 8, className, ...props }) {
  return (
    <span
      aria-hidden="true"
      className={cn("ui-tone-dot", toneClass(tone), className)}
      style={{ width: size, height: size }}
      {...props}
    />
  )
}

export { StatusPill, ToneDot }
